// Optional scheduling overlay attached to a Work Template. A Work Template
// with a schedule is a "PM"; without a schedule it's an ad-hoc template.

export type ScheduleCadence =
  | 'WEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'ANNUAL'
  | 'CUSTOM_DAYS';

export type DateFieldOperator =
  | 'DAYS_SINCE_GTE'
  | 'DAYS_UNTIL_LTE'
  | 'ON_OR_AFTER';

export type NumericFieldOperator = '>=' | '>' | '=' | '<=' | '<';

export type CalendarTrigger = {
  kind: 'CALENDAR';
  cadence: ScheduleCadence;
  // For CUSTOM_DAYS only.
  intervalDays?: number;
  // ISO date string. Anchors the cadence (first occurrence).
  anchorDate: string;
};

export type DateFieldTrigger = {
  kind: 'DATE_FIELD';
  attributeId: string;
  operator: DateFieldOperator;
  thresholdDays?: number;
  thresholdDate?: string;
};

export type NumericFieldTrigger = {
  kind: 'NUMERIC_FIELD';
  attributeId: string;
  // 'SINGLE' (default; preserves existing payloads) = fire once when the
  // value satisfies `operator threshold`.
  // 'INTERVAL' = fire each time the value crosses a new multiple of
  // `intervalAmount` measured from `intervalAnchor`.
  mode?: 'SINGLE' | 'INTERVAL';
  // SINGLE mode fields
  operator?: NumericFieldOperator;
  threshold?: number;
  // INTERVAL mode fields
  intervalAmount?: number;
  // First fire happens at `intervalAnchor + intervalAmount` (so anchor 0 +
  // every 5000 means fire at 5000, 10000, ...). Defaults to 0.
  intervalAnchor?: number;
};

export type ScheduleTrigger =
  | CalendarTrigger
  | DateFieldTrigger
  | NumericFieldTrigger;

// Helper: gather a template's schedules, supporting both the legacy single
// `schedule` field and the new `schedules` array. Use this everywhere instead
// of reading `.schedule` directly.
export const getTemplateSchedules = (
  t: { schedule?: WorkTemplateSchedule; schedules?: WorkTemplateSchedule[] },
): WorkTemplateSchedule[] => {
  if (t.schedules && t.schedules.length > 0) return t.schedules;
  if (t.schedule) return [t.schedule];
  return [];
};

export type WorkTemplateSchedule = {
  // Stable id so the UI can address one schedule among many on a template.
  // Generated by the server (or the mock handler) on first save. Optional
  // on the type so create payloads can be sent without one; always present
  // after a round-trip.
  id?: string;
  // Optional friendly label. If omitted, the UI derives a name from the
  // trigger (e.g. "Monthly", "Runtime Hours ≥ 14000").
  name?: string;
  active: boolean;
  // Inventory folder/category id. Schedule evaluates assets in this folder
  // and all of its descendants.
  targetCategoryId: string;
  trigger: ScheduleTrigger;
  leadTimeDays: number;
  // Other workTemplate IDs whose schedules this one supersedes when they
  // would both fire near the same time (e.g. quarterly supersedes
  // weekly + monthly).
  supersedes: string[];
  // Sliding day window around this schedule's firing date during which a
  // covered template is treated as already-handled. Defaults to 7 days.
  supersedesWindowDays?: number;
};

// Convenience: short, human-readable summary of a schedule (chip text on the
// Work Templates list, etc.).
export const summarizeSchedule = (
  schedule: WorkTemplateSchedule | undefined,
  attributeNameById?: Record<string, string>,
): string => {
  if (!schedule) return '—';
  const t = schedule.trigger;
  if (t.kind === 'CALENDAR') {
    if (t.cadence === 'CUSTOM_DAYS') return `Every ${t.intervalDays ?? 0} days`;
    const labels: Record<ScheduleCadence, string> = {
      WEEKLY: 'Weekly',
      MONTHLY: 'Monthly',
      QUARTERLY: 'Quarterly',
      ANNUAL: 'Annual',
      CUSTOM_DAYS: '',
    };
    return labels[t.cadence];
  }
  const attrName = attributeNameById?.[t.attributeId] ?? 'Field';
  if (t.kind === 'DATE_FIELD') {
    if (t.operator === 'DAYS_SINCE_GTE') {
      return `Days since ${attrName} ≥ ${t.thresholdDays}`;
    }
    if (t.operator === 'DAYS_UNTIL_LTE') {
      return `Days until ${attrName} ≤ ${t.thresholdDays}`;
    }
    return `${attrName} on/after ${t.thresholdDate}`;
  }
  // NUMERIC_FIELD
  if (t.mode === 'INTERVAL') {
    const amount = (t.intervalAmount ?? 0).toLocaleString();
    const anchor = t.intervalAnchor ?? 0;
    return anchor
      ? `${attrName} every ${amount} (from ${anchor.toLocaleString()})`
      : `${attrName} every ${amount}`;
  }
  return `${attrName} ${t.operator ?? '>='} ${(t.threshold ?? 0).toLocaleString()}`;
};
